import React from 'react';
import { FormProvider } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { CustomerType, DraftAccountDTO, FORM_WIDTH } from '../../AccountOpening.meta';
import { validation } from './Review.meta';
import { Footer } from '../../components/Footer/Footer';
import { Grid } from '../../../../CommonComponents/OPL/Grid/Grid';
import { Name } from '../../fields/Name/Name';
import { Address } from '../../fields/Address/Address';
import { DateOfBirth } from '../../fields/DateOfBirth/DateOfBirth';
import { Ssn } from '../../fields/Ssn/Ssn';
import { Phone } from '../../fields/Phone/Phone';
import { ReviewRow } from './components/ReviewRow/ReviewRow';
import { AnnualIncome } from '../../fields/NetWorthFields/AnnualIncome/AnnualIncome';
import { TotalNetWorth } from '../../fields/NetWorthFields/TotalNetWorth/TotalNetWorth';
import { LiquidNetWorth } from '../../fields/NetWorthFields/LiquidNetWorth/LiquidNetWorth';
import { ControlPerson } from '../../fields/ControlPerson/ControlPerson';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';
import { Email } from '../../fields/Email/Email';
import { AffiliatedExchange } from '../../fields/AffiliatedExchange/AffiliatedExchange';
import { EmploymentStatus } from '../../fields/EmploymentStatus/EmploymentStatus';
import { ESigned } from '../../fields/ESigned/ESigned';
import { useStepForm } from '../../hooks/useStepForm';
import { Beneficiary } from '../../fields/Beneficiary/Beneficiary';
import { StepContent } from '../../components/StepContent/StepContent';
import { Card } from '../../../../CommonComponents/OPL/Card/Card';
import { selectIsUserClient } from '../../../../CommonSelectors/CommonAreaSelector';
import { CustodianEmployee } from '../../fields/CustodianEmployee/CustodianEmployee';

interface Props {
  draftAccount: DraftAccountDTO;
  onSuccess(): void;
  onPrevious?(): void;
}

export const Review = ({ draftAccount, onSuccess, onPrevious }: Props) => {
  const { customerType } = draftAccount;

  const isUserClient = useSelector(selectIsUserClient);

  const { formBag, saveDraftMutation, onSubmit } = useStepForm({
    draftAccount,
    onSuccess,
    validation,
  });

  const isCustodianEmployee = formBag.watch('isCustodianEmployee');

  return (
    <FormProvider {...formBag}>
      <form onSubmit={formBag.handleSubmit(onSubmit)}>
        <StepContent>
          {isUserClient && (
            <Card p="32" maxWidth={FORM_WIDTH} mx="auto" mb="32">
              <ESigned draftAccount={draftAccount} />
            </Card>
          )}

          <Card py="32" maxWidth={FORM_WIDTH} mx="auto">
            <Text as="h2" fontSize="h5" fontWeight={600} px="32" mb="32">
              Please review your information
            </Text>

            {saveDraftMutation.error && (
              <Alert message={saveDraftMutation.error.message} variant="error" mb="32" mx="32" />
            )}

            <Grid px="8">
              <ReviewRow label="Name">
                <Name isReview fieldName="primaryApplicant" draftAccount={draftAccount} p="16" />
              </ReviewRow>

              <ReviewRow label="Date of Birth">
                <DateOfBirth
                  isReview
                  fieldName="primaryApplicant.dob"
                  draftAccount={draftAccount}
                  p="16"
                />
              </ReviewRow>

              <ReviewRow label="SSN">
                <Ssn isReview fieldName="primaryApplicant.ssn" draftAccount={draftAccount} p="16" />
              </ReviewRow>

              <ReviewRow label="Primary Phone">
                <Phone
                  isReview
                  fieldName="primaryApplicant.phoneNumber"
                  draftAccount={draftAccount}
                  p="16"
                />
              </ReviewRow>

              <ReviewRow label="Email">
                <Email
                  isReview
                  fieldName="primaryApplicant.email"
                  draftAccount={draftAccount}
                  p="16"
                />
              </ReviewRow>

              <ReviewRow label="Primary Address">
                <Address isReview fieldName="primaryApplicant" draftAccount={draftAccount} p="16" />
              </ReviewRow>

              {customerType === CustomerType.Joint && (
                <ReviewRow label="Secondary Holder">
                  <Name isReview fieldName="secondApplicant" draftAccount={draftAccount} p="16" />
                </ReviewRow>
              )}

              <ReviewRow label="Estimated Annual Income">
                <AnnualIncome isReview draftAccount={draftAccount} p="16" />
              </ReviewRow>

              <ReviewRow label="Estimated Liquid Net Worth">
                <LiquidNetWorth isReview draftAccount={draftAccount} p="16" />
              </ReviewRow>

              <ReviewRow label="Estimated Total Net Worth">
                <TotalNetWorth isReview draftAccount={draftAccount} p="16" />
              </ReviewRow>

              <ReviewRow label="Employment Status">
                <EmploymentStatus
                  isReview
                  fieldName="primaryApplicant"
                  draftAccount={draftAccount}
                  p="16"
                />
              </ReviewRow>

              <ReviewRow label="Director, 10% Shareholder, or Policy making officer of publicly traded company?">
                <ControlPerson isReview draftAccount={draftAccount} p="16" />
              </ReviewRow>

              {customerType === CustomerType.Ira && (
                <ReviewRow label="Would you like to add a beneficiary for this account?">
                  <Beneficiary isReview draftAccount={draftAccount} p="16" />
                </ReviewRow>
              )}

              <ReviewRow label="Is the account holder an employee of, or affiliated with, Apex Fintech Solutions?">
                <CustodianEmployee isReview draftAccount={draftAccount} p="16" />
              </ReviewRow>

              {!isCustodianEmployee && (
                <ReviewRow
                  label="Are you or your spouse, any member of your immediate family, including parents, in-laws, siblings, and dependents licensed, employed by, or associated with, a broker-dealer firm, a financial services regulator, securities exchange, or member of a securities exchange?"
                  borderBottom="none"
                >
                  <AffiliatedExchange isReview draftAccount={draftAccount} p="16" />
                </ReviewRow>
              )}
            </Grid>
          </Card>
        </StepContent>

        <Footer
          isLoading={saveDraftMutation.isLoading}
          onPrevious={onPrevious}
          nextButtonText={isUserClient ? 'Confirm' : 'Send to client'}
        />
      </form>
    </FormProvider>
  );
};
